import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import { pollingInterval } from "../../../constants/pollingInterval";
import { AuthContext } from "../../../context/AuthContext";
import { DataContext } from "../../../context/DataContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import {
  IAccountIsolatedHealth,
  IIsolatedHealthPosition,
  IIsolatedHealthSummary,
  IsolatedRiskStatus,
} from "../../../interfaces/aevo";
import { compareDecimalStrings } from "../../../utils/decimal";
import { ADMIN_API_URL } from "../../../utils/env";

interface IAccountIsolatedHealthApiResponse {
  summary?: Partial<IIsolatedHealthSummary>;
  account?: string | Partial<IIsolatedHealthSummary>;
  assigned_isolated_margin?: string;
  isolated_available_margin?: string;
  isolated_positions_count?: number;
  breached_positions_count?: number;
  liquidating_positions_count?: number;
  positions?: Array<Partial<IIsolatedHealthPosition>>;
}

const RISK_STATUSES: IsolatedRiskStatus[] = [
  "healthy",
  "warning",
  "breached",
  "liquidating",
];

const isRiskStatus = (value: unknown): value is IsolatedRiskStatus =>
  typeof value === "string" &&
  RISK_STATUSES.includes(value as IsolatedRiskStatus);

const asString = (value: unknown, fallback: string = "0"): string => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return String(value);
};

const asNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const deriveRiskStatus = (
  buffer: string,
  inLiquidation: boolean,
  riskStatus?: unknown
): IsolatedRiskStatus => {
  if (inLiquidation) {
    return "liquidating";
  }

  if (isRiskStatus(riskStatus)) {
    return riskStatus;
  }

  if (compareDecimalStrings(buffer, "0") < 0) {
    return "breached";
  }

  if (compareDecimalStrings(buffer, "10") <= 0) {
    return "warning";
  }

  return "healthy";
};

const normalizePosition = (
  position: Partial<IIsolatedHealthPosition>
): IIsolatedHealthPosition => {
  const inLiquidation = Boolean(position.in_liquidation);
  const buffer = asString(position.buffer);

  return {
    instrument_id: asNumber(position.instrument_id),
    instrument_name: asString(position.instrument_name, "-"),
    asset: asString(position.asset, "-"),
    position: asString(position.position),
    entry_price: asString(position.entry_price),
    mark_price: asString(position.mark_price),
    allocated_isolated_margin: asString(position.allocated_isolated_margin),
    base_isolated_margin: asString(position.base_isolated_margin),
    upnl: asString(position.upnl),
    maintenance_margin: asString(position.maintenance_margin),
    buffer,
    liquidation_price: asString(position.liquidation_price, "-"),
    in_liquidation: inLiquidation,
    risk_status: deriveRiskStatus(buffer, inLiquidation, position.risk_status),
  };
};

const normalizeResponse = (
  payload: IAccountIsolatedHealthApiResponse,
  requestedAccount: string
): IAccountIsolatedHealth => {
  const accountObject =
    payload.account && typeof payload.account === "object" ?
      payload.account :
      undefined;
  const summarySource = payload.summary || accountObject || {};

  const summary: IIsolatedHealthSummary = {
    account:
      (typeof payload.account === "string" ?
        payload.account :
        undefined) || asString(summarySource.account, requestedAccount),
    assigned_isolated_margin: asString(
      summarySource.assigned_isolated_margin ?? payload.assigned_isolated_margin
    ),
    isolated_available_margin: asString(
      summarySource.isolated_available_margin ??
        payload.isolated_available_margin
    ),
    isolated_positions_count: asNumber(
      summarySource.isolated_positions_count ??
        payload.isolated_positions_count
    ),
    breached_positions_count: asNumber(
      summarySource.breached_positions_count ??
        payload.breached_positions_count
    ),
    liquidating_positions_count: asNumber(
      summarySource.liquidating_positions_count ??
        payload.liquidating_positions_count
    ),
  };

  return {
    summary,
    positions: (payload.positions || []).map(normalizePosition),
  };
};

export const useAccountIsolatedHealth = (overrideAccount?: string) => {
  const { address } = useContext(DataContext);
  const { queryConfig, tokenId } = useContext(AuthContext);
  const account = overrideAccount || address;
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.ACCOUNT_ISOLATED_HEALTH);

  const swr = useSWR<IAccountIsolatedHealth, AxiosError>(
    tokenId && account ? [endpoint, tokenId, account] : undefined,
    async () => {
      const response = await axios.get<IAccountIsolatedHealthApiResponse>(
        endpoint,
        {
          ...queryConfig(),
          params: { account },
        }
      );

      return normalizeResponse(response.data, account || "");
    },
    {
      refreshInterval: pollingInterval[AdminEndpointEnum.ACCOUNT_ISOLATED_HEALTH],
    }
  );

  return {
    ...swr,
  };
};
